import {
  Spinner,
  Text,
  View,
  XStack,
  YStack,
  isWeb,
  styled,
  useMedia,
} from 'tamagui'
import {
  useAudioPlayer,
  useAudioPlayerStatus,
  setAudioModeAsync,
} from 'expo-audio'
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake'
import { LinearGradient } from 'tamagui/linear-gradient'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HeroIcon } from '@components/HeroIcon'
import { RangeInput } from '@components/RangeInput'
import { useThemeColor } from '@hooks/useThemeColor'
import SoundtrackSvg from '@assets/hero/soundtrack.svg'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { PlaybackRateSelector } from '@components/VideoPlayer/PlaybackRateSelector'
import { PLAYBACK_RATE_OPTIONS } from '@components/VideoPlayer'
import { IUpdateLessonProgress } from '@components/MediaRender'
import { Platform, useWindowDimensions } from 'react-native'

interface IAudioPlayerProps {
  url: string
  updateProgress: (params: IUpdateLessonProgress) => void
  actualProgress?: number
  audioStartTime?: number
}

const PROGRESS_THRESHOLDS = [
  { threshold: 25, key: 25 as const },
  { threshold: 50, key: 50 as const },
  { threshold: 75, key: 75 as const },
  { threshold: 100, key: 100 as const },
]

export function AudioPlayer({
  url,
  updateProgress,
  actualProgress = 0,
  audioStartTime,
}: IAudioPlayerProps) {
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false)
  const [showPlaybackRateSelector, setShowPlaybackRateSelector] =
    useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  // Waveform (web) em px explícitos — react-native-svg não escala de forma
  // confiável com width="%". Usa a largura da janela (sempre disponível, sem
  // depender de onLayout) e mantém o aspecto 328x108.
  const { width: windowWidth } = useWindowDimensions()
  const media = useMedia()
  // Desktop: cresce com a janela (mín. 800). Mobile-web: cabe na tela (90%).
  const waveformWidth = media.gtSm
    ? Math.max(800, Math.round(windowWidth * 0.6))
    : Math.round(windowWidth * 0.9)
  const waveformHeight = Math.round((waveformWidth * 120) / 328)
  const [progressMarkers, setProgressMarkers] = useState({
    25: actualProgress >= 25,
    50: actualProgress >= 50,
    75: actualProgress >= 75,
    100: actualProgress >= 100,
  })

  const player = useAudioPlayer(url, { updateInterval: 1000 })
  const { playing, duration, currentTime, isBuffering } =
    useAudioPlayerStatus(player)
  const rangeColor = useThemeColor('accent')
  const pageFade = useThemeColor('bg-page', 0.6)
  const updateProgressRef = useRef(updateProgress)

  useEffect(() => {
    updateProgressRef.current = updateProgress
  }, [updateProgress])

  useEffect(() => {
    // expo-keep-awake quebra no web ("wake lock ... has not activated yet").
    // Manter tela acordada só faz sentido no nativo.
    if (isWeb) return
    if (!playing) {
      deactivateKeepAwake()
      return
    }
    activateKeepAwakeAsync()

    return () => {
      deactivateKeepAwake()
    }
  }, [playing])

  useEffect(() => {
    if (!duration || duration <= 0) return

    const progress = (currentTime / duration) * 100

    PROGRESS_THRESHOLDS.forEach(({ threshold, key }) => {
      if (!progressMarkers[key] && progress >= threshold) {
        setProgressMarkers((prev) => ({ ...prev, [key]: true }))

        updateProgressRef.current({
          progress: threshold,
          currentVideoTime: Math.floor(currentTime),
        })
      }
    })
  }, [currentTime, duration, progressMarkers])

  const handlePlay = useCallback(() => {
    if (playing) {
      player.pause()
    } else {
      if (audioStartTime && !hasStartedPlaying) {
        player.seekTo(audioStartTime)
      }
      setHasStartedPlaying(true)
      player.play()
    }
  }, [player, playing, audioStartTime, hasStartedPlaying])

  const playAndPauseIcon: IconProp = useMemo(() => {
    return playing ? ['fas', 'pause'] : ['fas', 'play']
  }, [playing])

  const formatTime = useMemo(() => {
    const hours = Math.floor(currentTime / 3600)
    const minutes = Math.floor((currentTime % 3600) / 60)
    const seconds = Math.floor(currentTime % 60)

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }, [currentTime])

  // RangeInput opera em porcentagem (0-100), não em segundos: com duration
  // indisponível (falsy/0 — comum no boot do player ou enquanto o áudio
  // carrega), max={duration || 1} deixava o range preso em [0, 1], travando
  // o slider. VideoPlayer/BottomControls já usa esse mesmo padrão percentual.
  const currentTimePercentage = useMemo(() => {
    if (!duration || duration <= 0) return 0
    return (currentTime / duration) * 100
  }, [currentTime, duration])

  // RangeInput (web) é um <input type="range"> controlado: sem estado local
  // de arraste, `value` fica preso em currentTimePercentage e o player emite
  // status a cada 1s (useAudioPlayer(url, 1000)), forçando o thumb de volta
  // pra posição real a cada re-render — o usuário arrasta mas o slider some
  // ou volta, dando a impressão de travado. Enquanto arrasta, usa o valor
  // local; solta, comita o seek e volta a seguir o currentTime real.
  const [draggingPercentage, setDraggingPercentage] = useState<number | null>(
    null,
  )

  const handleProgressChange = useCallback(
    (percentage: number) => {
      setDraggingPercentage(null)
      if (!duration || duration <= 0) return
      player.seekTo((percentage / 100) * duration)
    },
    [player, duration],
  )

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      player.shouldCorrectPitch = true
      player.setPlaybackRate(rate)
      setPlaybackRate(rate)
    },
    [player],
  )

  useEffect(() => {
    const configureAudioSession = async () => {
      if (Platform.OS === 'ios') {
        try {
          await setAudioModeAsync({
            playsInSilentMode: true,
            interruptionMode: 'mixWithOthers',
          })
        } catch (error) {
          console.error('Erro ao configurar modo de áudio:', error)
        }
      }
    }

    configureAudioSession()
  }, [])

  return (
    <View
      key={url}
      height="100%"
      width="100%"
      alignItems="center"
      justifyContent="center"
    >
      {isWeb ? (
        // Web: waveform centralizado (horizontal + vertical), em px explícitos
        // (~85% da largura do container, aspecto 328x108). Espaço p/ controles.
        <View
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={-48}
          alignItems="center"
          justifyContent="center"
        >
          <SoundtrackSvg width={waveformWidth} height={waveformHeight} />
        </View>
      ) : (
        // Mobile: waveform centralizado em px (mesmo aspecto do web). Evita o
        // stretch/overflow que o height="100%" causava com o SVG grande.
        <View
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          alignItems="center"
          justifyContent="center"
        >
          <SoundtrackSvg width={waveformWidth} height={waveformHeight} />
        </View>
      )}
      <LinearGradient
        colors={['transparent', pageFade]}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
        }}
      />
      <MediaControl
        onPress={handlePlay}
        position="absolute"
        top="50%"
        right="50%"
        transform={[{ translateY: -24 }, { translateX: 24 }]}
        zIndex={1}
      >
        {isBuffering ? (
          <Spinner size="large" color="$text-on-media" />
        ) : (
          <HeroIcon icon={playAndPauseIcon} size={32} color="$text-on-media" />
        )}
      </MediaControl>
      <YStack
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        paddingHorizontal="$spacing-inset-xs"
        paddingVertical="$spacing-inset-nano"
        gap="$spacing-inset-nano"
      >
        <XStack justifyContent="space-between" alignItems="center">
          <View>
            <Text
              color="$text-on-media"
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
            >
              {formatTime}
            </Text>
          </View>
          <MediaControl onPress={() => setShowPlaybackRateSelector(true)}>
            <HeroIcon icon={['fal', 'cog']} size={24} color="$text-on-media" />
          </MediaControl>
        </XStack>
        <View>
          <RangeInput
            rangeColor={rangeColor}
            foregroundColor="rgba(207, 207, 207, 0.40)"
            value={draggingPercentage ?? currentTimePercentage}
            onValueChange={setDraggingPercentage}
            onSlidingComplete={handleProgressChange}
          />
        </View>
      </YStack>
      <PlaybackRateSelector
        open={showPlaybackRateSelector}
        onClose={() => setShowPlaybackRateSelector(false)}
        playbackRateOptions={PLAYBACK_RATE_OPTIONS}
        onPlaybackRateChange={handlePlaybackRateChange}
        currentPlaybackRate={playbackRate}
      />
    </View>
  )
}

const MediaControl = styled(View, {
  role: 'button',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  padding: '$spacing-inset-xxs',
})
